import { datadogRum } from '@datadog/browser-rum'
import cookie from 'js-cookie'
import posthog from 'posthog-js'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ConfigManager } from '~/managers/ConfigManager'
import { ModeManager } from '~/managers/ModeManager'
import SSOManager from '~/managers/SSOManager'
import { fetchAssetList } from '~/store/assets/actions'
import { selectIsAccountListInitiated } from '~/store/assets/selectors'
import { fetchBankList } from '~/store/bankAccounts/actions'
import {
  fetchBlockchainAddresses,
  setBlockchainAddressList,
} from '~/store/blockchainAddresses/actions'
import { FLIPPER_KEYS } from '~/store/flippers/const'
import {
  selectFlipperWithId,
  selectIsDpt2TncEnabled,
  selectIsDirectMintBizPhase1Enabled,
  selectIsOtcTrxHistoryEnabled,
} from '~/store/flippers/selectors'
import { selectUserBizId, selectUserEmail } from '~/store/route/selectors'
import { fetchLatestTransactions } from '~/store/transactions/actions'
import { USER_ROLES } from '~/types'
import { selectDgwMerchantAccount } from './store/accounts/selectors'
import { fetchMerchantDetail, fetchUserInfo, fetchUserProfile } from './store/merchantInfo/actions'
import {
  selectBusinessVerificationStatus,
  selectIsDpt2TncAccepted,
  selectIsKillSwitchActive,
  selectUserId,
  selectUserInfo,
} from './store/merchantInfo/selectors'
import { init } from './store/route/actions'
import { selectFeatureMatrixFactors } from './store/route/selectors'
import { getTotalTravelRuleComplianceBlockerCount } from './store/straitsXTransactions/actions'
import { RootState } from './store/types'
import { selectIsAccountStatementUIEnabled } from '~/store/accountStatement/selectors'

export const useInitRoot = () => {
  const dispatch = useDispatch()
  const isSandbox = ModeManager.isSandboxMode()

  const isAccountListInitiated = useSelector(selectIsAccountListInitiated)
  const {
    isInitiated,
    notifications: fetchedNotifications,
    isUserInfoInitialised,
    isAccountEnabled,
  } = useSelector((state: RootState) => state.route)
  const bizId = useSelector(selectUserBizId)
  const isOtcTrxHistoryEnabled = useSelector(selectIsOtcTrxHistoryEnabled)
  const bizAccount = useSelector(selectDgwMerchantAccount)
  const businessVerificationStatus = useSelector(selectBusinessVerificationStatus)
  const { role } = useSelector(selectFeatureMatrixFactors)
  const userId = useSelector(selectUserId)
  const isDpt2TncAccepted = useSelector(selectIsDpt2TncAccepted)
  const isKillSwitchActive = useSelector(selectIsKillSwitchActive)
  const email = useSelector(selectUserEmail)
  const isDpt2TncEnabled = useSelector(selectIsDpt2TncEnabled)
  const flipperDisableManageAccountsPage = useSelector(
    selectFlipperWithId(FLIPPER_KEYS.STRAITSX_SANDBOX_DISABLE_MANAGE_ACCOUNTS_PAGE, email)
  )
  const { userInfo } = useSelector(selectUserInfo)
  const isAccountStatementUIEnabled = useSelector(selectIsAccountStatementUIEnabled)
  const isBizMintUIEnabled = useSelector(selectIsDirectMintBizPhase1Enabled)

  const [displayGetStartedTab, setDisplayGetStartedTab] = useState(false)

  const notifications = useMemo(
    () => ({
      isLoading: fetchedNotifications.isLoading,
      data: fetchedNotifications.data,
      unreadCount: fetchedNotifications.unreadCount,
    }),
    [fetchedNotifications.isLoading]
  )

  const isCardsDashboardEnabled = useMemo(() => {
    return userInfo?.cardsDashboardEnabled || false
  }, [userInfo])

  const isStraitsxMenuDisabled = useMemo(() => {
    return userInfo?.straitsxMenuDisabled || false
  }, [userInfo])

  const shouldRenderDpt2Tnc = useMemo(() => {
    return (
      !isDpt2TncAccepted &&
      isUserInfoInitialised &&
      !isKillSwitchActive &&
      isDpt2TncEnabled &&
      !isSandbox
    )
  }, [isDpt2TncAccepted, isUserInfoInitialised, isKillSwitchActive, isDpt2TncEnabled, isSandbox])

  const shouldRenderCardsDashboard = useMemo(() => {
    return isCardsDashboardEnabled && isStraitsxMenuDisabled
  }, [isCardsDashboardEnabled, isStraitsxMenuDisabled])

  const shouldRenderManageAccounts = useMemo(() => {
    return isSandbox && flipperDisableManageAccountsPage?.enabled
  }, [isSandbox, flipperDisableManageAccountsPage])

  useEffect(() => {
    dispatch(fetchUserInfo())
    dispatch(fetchBankList())
    dispatch(fetchMerchantDetail())
    dispatch(fetchUserProfile())
    dispatch(getTotalTravelRuleComplianceBlockerCount())
    dispatch(fetchAssetList())
  }, [])

  useEffect(() => {
    if (!isAccountListInitiated || !isOtcTrxHistoryEnabled) {
      return
    }

    dispatch(fetchLatestTransactions())
  }, [isAccountListInitiated, isOtcTrxHistoryEnabled])

  useEffect(() => {
    if (!isAccountListInitiated) return

    if (bizAccount?.accountId) {
      dispatch(fetchBlockchainAddresses(bizAccount.accountId))
    } else {
      dispatch(setBlockchainAddressList([]))
    }
  }, [dispatch, isAccountListInitiated, bizAccount?.accountId])

  useEffect(() => {
    if (!isAccountEnabled) {
      cookie.remove('SESSIONID')
      SSOManager.ssoLogout()
    }
  }, [isAccountEnabled])

  useEffect(() => {
    const isVerificationPending =
      businessVerificationStatus && businessVerificationStatus !== 'verified'

    if (isSandbox) {
      setDisplayGetStartedTab(false)
      return
    }

    if (isVerificationPending && role === USER_ROLES.OWNER) {
      setDisplayGetStartedTab(true)
    }
  }, [isSandbox, businessVerificationStatus, role])

  useEffect(() => {
    if (!isInitiated) dispatch(init())
  }, [isInitiated]) // Run init just once

  useEffect(() => {
    if (ConfigManager.isProd() && userId) {
      datadogRum.setUser({
        straitsx_user_id: userId,
      })
    }

    if (ConfigManager.isProd() && userId && bizId) {
      posthog.identify(posthog.get_distinct_id(), { straitsx_user_id: userId, bizId })
    }
  }, [userId, bizId])

  return {
    displayGetStartedTab,
    isBizMintUIEnabled,
    isAccountStatementUIEnabled,
    isCardsDashboardEnabled,
    isInitiated,
    isKillSwitchActive,
    isStraitsxMenuDisabled,
    notifications,
    shouldRenderCardsDashboard,
    shouldRenderDpt2Tnc,
    shouldRenderManageAccounts,
  }
}
